import { type UseModalState } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type EnabledEmergencyCreditsConfig } from "@src/appV2/redesign/ClipboardScore/hooks/useEmergencyCreditsEducation";
import { Button } from "@src/appV2/redesign/components/Button";
import { ProgressBars } from "@src/appV2/redesign/components/ProgressBars";
import { TopRightCloseButton } from "@src/appV2/redesign/components/TopRightCloseButton";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type ReactNode, useState } from "react";

import { EarningCreditsStep } from "./EarningCreditsStep";
import { IntroStep } from "./IntroStep";
import { OneCreditEachStep } from "./OneCreditEachStep";
import { ScheduleChangesStep } from "./ScheduleChangesStep";

interface EmergencyCreditsEducationContainerProps {
  readonly modalState: UseModalState;
  readonly markAsSeen: () => void;
  readonly showCloseButton: boolean;
  readonly emergencyCreditsConfig: EnabledEmergencyCreditsConfig;
}

interface SlideProps {
  readonly content: ReactNode;
}

function Slide(props: SlideProps) {
  const { content } = props;

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      spacing={5}
      overflow="auto"
      flex={1}
      minHeight={0}
      display="flex"
      flexDirection="column"
    >
      {content}
    </Stack>
  );
}

export function EmergencyCreditsEducationContainer(props: EmergencyCreditsEducationContainerProps) {
  const { modalState, markAsSeen, showCloseButton, emergencyCreditsConfig } = props;
  const [step, setStep] = useState(0);
  const totalSteps = 4;
  const { userId: workerId } = useDefinedWorker();

  useLogEffect(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_STARTED, { workerId });

  const handleExit = () => {
    logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_EXITED, { workerId, step });
    modalState.closeModal();
    setStep(0);
  };

  const handleBack = () => {
    if (step === 0) {
      handleExit();
    } else {
      logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_STEP_VIEWED, {
        workerId,
        step: step - 1,
        action: "back",
      });
      setStep(step - 1);
    }
  };

  const handleNext = () => {
    if (step < totalSteps - 1) {
      logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_STEP_VIEWED, {
        workerId,
        step: step + 1,
        action: "next",
      });
      setStep(step + 1);
    } else {
      logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_EDUCATION_COMPLETED, { workerId });
      markAsSeen();
      handleExit();
    }
  };

  return (
    <FullScreenDialog modalState={modalState}>
      {showCloseButton && <TopRightCloseButton onClick={handleExit} />}
      <Stack
        spacing={6}
        alignItems="center"
        sx={{
          width: "100%",
          height: "100%",
          justifyContent: "space-between",
          paddingX: 5,
          paddingTop: 8,
          paddingBottom: "calc(16px + env(safe-area-inset-bottom))",
        }}
      >
        {step === 0 && <Slide content={<IntroStep />} />}
        {step === 1 && <Slide content={<OneCreditEachStep />} />}
        {step === 2 && <Slide content={<ScheduleChangesStep />} />}
        {step === 3 && (
          <Slide content={<EarningCreditsStep emergencyCreditsConfig={emergencyCreditsConfig} />} />
        )}

        <ProgressBars currentStep={step} totalSteps={totalSteps} />
        {step === 0 ? (
          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={{ borderRadius: 999, height: 56 }}
            onClick={handleNext}
          >
            Next
          </Button>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", maxWidth: 500, margin: "0 auto" }}
          >
            <Button
              size="large"
              variant="outlined"
              sx={{ borderRadius: 999, height: 56, flex: 1, maxWidth: 220 }}
              onClick={handleBack}
            >
              Back
            </Button>
            <Button
              size="large"
              variant="contained"
              sx={{ borderRadius: 999, height: 56, flex: 1, maxWidth: 220 }}
              onClick={handleNext}
            >
              {step < totalSteps - 1 ? "Next" : "Finish"}
            </Button>
          </Stack>
        )}
      </Stack>
    </FullScreenDialog>
  );
}
